import { getEnvByHostname } from '../utils/env';
import React, { useState } from 'react';
import { Card, Form, Input, Button, Space, message, Descriptions, Tag, Empty, Row, Col, Statistic, Timeline } from 'antd';
import { SearchOutlined, ReloadOutlined, DollarOutlined, CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined, SyncOutlined, ExportOutlined } from '@ant-design/icons';
import { useLanguage } from '../contexts/LanguageContext';
import { usePageCache } from '../contexts/PageCacheContext';
import { testApi } from '../services/api';
import dayjs from 'dayjs';

const fmt = (ts) => ts ? dayjs(Number(ts)).format('YYYY-MM-DD HH:mm:ss') : '-';

export default function WithdrawQuery() {
  const [form] = Form.useForm();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [withdrawData, setWithdrawData] = useState(null);
  const { cache, save: saveCache } = usePageCache('withdrawQuery');

  React.useEffect(() => {
    if (cache) {
      if (cache.withdrawData) setWithdrawData(cache.withdrawData);
      if (cache.formValues) form.setFieldsValue(cache.formValues);
    }
  }, []);
  React.useEffect(() => {
    return () => { saveCache({ withdrawData, formValues: form.getFieldsValue() }); };
  });

  const handleQuery = async (values) => {
    setLoading(true);
    try {
      const requestBody = { orderId: values.orderId, requestId: values.requestId || `req_${Date.now()}` };
      const { data } = await testApi.executeTest({
        businessPartner: 'DEMO_ACCOUNT', env: getEnvByHostname(),
        testType: 'PAYOUT_QUERY', requestBody: JSON.stringify(requestBody),
        executor: localStorage.getItem('username')
      });
      const res = data.data?.responseBody ? JSON.parse(data.data.responseBody) : data;
      if (res.code === '200' || res.code === 200) { setWithdrawData(res); }
      else { message.error(`${t('queryFailed')}: ${res.msg || t('queryFailed')}`); setWithdrawData(null); }
    } catch (error) {
      message.error(error.response?.data?.msg || t('queryFailed'));
    } finally { setLoading(false); }
  };

  const getStatusTag = (status) => {
    const cfg = { SUCCESS: { color: '#52c41a', icon: <CheckCircleOutlined /> }, PROCESSING: { color: '#1890ff', icon: <SyncOutlined spin /> }, FAILED: { color: '#ff4d4f', icon: <CloseCircleOutlined /> }, PENDING: { color: '#faad14', icon: <ClockCircleOutlined /> } };
    const c = cfg[status] || cfg.PENDING;
    return <Tag color={c.color} icon={c.icon} style={{ fontSize: 14, padding: '4px 12px' }}>{status}</Tag>;
  };

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1400, margin: '0 auto' }}>
      <div className="fade-in-up">
        <Card className="modern-card"
          title={<Space size={16}><SearchOutlined style={{ color: '#667eea', fontSize: 20 }} /><span style={{ fontSize: 20, fontWeight: 600 }}>{t('payoutQuery')}</span></Space>}
          extra={<Button icon={<ReloadOutlined />} onClick={() => { form.resetFields(); setWithdrawData(null); }} type="text">{t('reset')}</Button>}
          style={{ marginBottom: 24 }}>
          <Form form={form} layout="vertical" onFinish={handleQuery} size="large">
            <Row gutter={24}>
              <Col xs={24} sm={12}>
                <Form.Item label={<span style={{ fontWeight: 500 }}>{t('payoutOrderId')}</span>} name="orderId"
                  rules={[{ required: true, message: t('pleaseInput', t('payoutOrderId')) }]}>
                  <Input placeholder="e.g. WD20251218EM00000020" prefix={<ExportOutlined style={{ color: '#9ca3af' }} />} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item label={<span style={{ fontWeight: 500 }}>Request ID ({t('optional')})</span>} name="requestId">
                  <Input placeholder={t('autoGenerate')} />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />} loading={loading} className="primary-button" style={{ height: 48 }}>{t('search')}</Button>
            </Form.Item>
          </Form>
        </Card>

        {withdrawData?.data ? (
          <div className="fade-in-up">
            <Row gutter={16} style={{ marginBottom: 24 }}>
              {[
                { title: t('payoutStatus'), value: withdrawData.data.status, icon: <SyncOutlined spin={withdrawData.data.status === 'PROCESSING'} style={{ color: '#667eea' }} />, fontSize: 18 },
                { title: t('payoutAmount'), value: withdrawData.data.amount, icon: <DollarOutlined style={{ color: '#667eea' }} />, fontSize: 24, suffix: withdrawData.data.currency },
                { title: t('fee'), value: withdrawData.data.fee ?? '-', fontSize: 24, suffix: withdrawData.data.fee ? withdrawData.data.currency : '' },
                { title: t('withdrawMethod'), value: withdrawData.data.withdrawMethod, fontSize: 13 },
              ].map((s, i) => (
                <Col xs={24} sm={12} md={6} key={i}>
                  <Card className="modern-card" style={{ background: '#fff', borderLeft: '4px solid #667eea' }}>
                    <Statistic title={<span style={{ color: '#6b7280', fontSize: 14 }}>{s.title}</span>} value={s.value}
                      valueStyle={{ color: '#1f2937', fontSize: s.fontSize, fontWeight: 600 }} prefix={s.icon} suffix={s.suffix} />
                  </Card>
                </Col>
              ))}
            </Row>

            <Card className="modern-card"
              title={<Space><ExportOutlined style={{ color: '#667eea' }} /><span style={{ fontSize: 18, fontWeight: 600 }}>{t('payoutOrderDetail')}</span>{getStatusTag(withdrawData.data.status)}</Space>}
              style={{ marginBottom: 24 }}>
              <Descriptions column={{ xs: 1, sm: 2, md: 3 }} bordered size="middle">
                <Descriptions.Item label={t('channelPayoutOrderId')} span={2}><code style={{ background: '#f5f5f5', padding: '4px 8px', borderRadius: 4, fontSize: 14 }}>{withdrawData.data.orderId}</code></Descriptions.Item>
                <Descriptions.Item label={t('status')}>{getStatusTag(withdrawData.data.status)}</Descriptions.Item>
                <Descriptions.Item label={t('externalPayoutOrderId')} span={2}><code style={{ background: '#f5f5f5', padding: '2px 6px', borderRadius: 4 }}>{withdrawData.data.externalOrderId}</code></Descriptions.Item>
                <Descriptions.Item label={t('subStatus')}><Tag color="blue">{withdrawData.data.subStatus || '-'}</Tag></Descriptions.Item>
                <Descriptions.Item label={t('channelCode')}><Tag color="purple">{withdrawData.data.channelCode}</Tag></Descriptions.Item>
                <Descriptions.Item label={t('recipientId')}><code style={{ background: '#f5f5f5', padding: '2px 6px', borderRadius: 4 }}>{withdrawData.data.recipientId}</code></Descriptions.Item>
                <Descriptions.Item label={t('withdrawMethod')}><Tag color="green">{withdrawData.data.withdrawMethod}</Tag></Descriptions.Item>
                <Descriptions.Item label={t('currency')}><Tag color="orange">{withdrawData.data.currency}</Tag></Descriptions.Item>
                <Descriptions.Item label={t('amount')}><span style={{ fontSize: 16, fontWeight: 600, color: '#ff4d4f' }}>{withdrawData.data.amount} {withdrawData.data.currency}</span></Descriptions.Item>
                <Descriptions.Item label={t('fee')}><span style={{ fontSize: 16, fontWeight: 600, color: '#ff4d4f' }}>{withdrawData.data.fee ? `${withdrawData.data.fee} ${withdrawData.data.currency}` : '-'}</span></Descriptions.Item>
                {withdrawData.data.errorCode && <Descriptions.Item label={t('errorCode')}><Tag color="error">{withdrawData.data.errorCode}</Tag></Descriptions.Item>}
                {withdrawData.data.errorMsg && <Descriptions.Item label={t('errorDetail')} span={withdrawData.data.errorCode ? 2 : 3}><pre style={{ margin: 0, padding: '8px 12px', background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 6, fontSize: 13, color: '#ff4d4f', fontFamily: 'Monaco, Consolas, monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 200, overflow: 'auto' }}>{(() => { try { return JSON.stringify(JSON.parse(withdrawData.data.errorMsg), null, 2); } catch (e) { return withdrawData.data.errorMsg; } })()}</pre></Descriptions.Item>}
                <Descriptions.Item label={t('createTime')}>{fmt(withdrawData.data.createTime)}</Descriptions.Item>
                <Descriptions.Item label={t('completedTime')}>{fmt(withdrawData.data.completedTime)}</Descriptions.Item>
              </Descriptions>
            </Card>

            <Card className="modern-card" title={<Space><DollarOutlined style={{ color: '#667eea' }} /><span style={{ fontSize: 18, fontWeight: 600 }}>{t('callbackConfig')}</span></Space>} style={{ marginBottom: 24 }}>
              <Descriptions column={1} size="middle">
                <Descriptions.Item label={<span style={{ fontWeight: 500 }}>{t('callbackUrl')}</span>}>
                  <a href={withdrawData.data.callbackUrl} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all' }}>{withdrawData.data.callbackUrl || '-'}</a>
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <Card className="modern-card" title={<Space><ClockCircleOutlined style={{ color: '#667eea' }} /><span style={{ fontSize: 18, fontWeight: 600 }}>{t('payoutOrderTimeline')}</span></Space>}>
              <Timeline items={[
                withdrawData.data.completedTime && { color: 'green', children: <div><div style={{ fontWeight: 600, marginBottom: 4 }}>{t('payoutCompleted')}</div><div style={{ color: '#6b7280', fontSize: 14 }}>{fmt(withdrawData.data.completedTime)}</div></div> },
                { color: withdrawData.data.status === 'PROCESSING' ? 'blue' : 'gray', children: <div><div style={{ fontWeight: 600, marginBottom: 4 }}>{t('payoutProcessing')}</div><div style={{ color: '#6b7280', fontSize: 14 }}>{t('status')}: {withdrawData.data.subStatus || withdrawData.data.status}</div></div> },
                { color: 'blue', children: <div><div style={{ fontWeight: 600, marginBottom: 4 }}>{t('orderCreated')}</div><div style={{ color: '#6b7280', fontSize: 14 }}>{fmt(withdrawData.data.createTime)}</div></div> },
              ].filter(Boolean)} />
            </Card>
          </div>
        ) : (
          <Card className="modern-card">
            <Empty description={<span style={{ color: '#6b7280', fontSize: 16 }}>{t('enterPayoutOrderId')}</span>} image={Empty.PRESENTED_IMAGE_SIMPLE} />
          </Card>
        )}
      </div>
    </div>
  );
}
